import { useState } from 'react';
import type React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import type { Analysis } from '../../types';

interface AnalysisResultProps {
  analysis: Analysis;
}

export const AnalysisResult = ({ analysis }: AnalysisResultProps) => {
  const [copied, setCopied] = useState(false);

  // Parse markdown to React nodes
  const parseMarkdown = (text: string, uniquePrefix?: string): React.ReactNode => {
    if (!text) return null;

    // Bold: **text** or __text__
    let parsed = text.split(/(\*\*.*?\*\*|__.*?__)/g).map((part, i) => {
      const key = uniquePrefix ? `${uniquePrefix}-markdown-${i}` : `markdown-${Date.now()}-${i}`;
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={key} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return <strong key={key} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      }
      return <span key={key}>{part}</span>;
    });

    return <>{parsed}</>;
  };

  // Format content with better structure for new format
  const formatContent = (content: string, sectionType?: string, sectionId?: string | number): React.ReactNode => {
    if (!content) return null;

    const lines = content.split('\n').filter(Boolean);
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];
    let inSourcesSection = sectionType?.toLowerCase().includes('fontes');
    let listCounter = 0;
    let elementCounter = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        const listId = `list-${sectionId || 'default'}-${listCounter++}`;
        elements.push(
          <ul key={listId} className="list-disc list-inside space-y-2 mb-4 ml-4">
            {currentList.map((item, idx) => (
              <li key={`${listId}-item-${idx}`} className="text-gray-300">{parseMarkdown(item, `${listId}-item-${idx}`)}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        flushList();
        return;
      }

        const uniqueKey = `${sectionId || 'default'}-${elementCounter++}`;

      // Detect sub-headers (bold text at start of line)
      if (trimmedLine.match(/^\*\*(.+?)\*\*[:]?\s*$/)) {
        flushList();
        const match = trimmedLine.match(/^\*\*(.+?)\*\*[:]?\s*$/);
        if (match) {
          elements.push(
            <h4 key={`subheader-${uniqueKey}`} className="text-lg font-semibold text-white mt-4 mb-2">
              {match[1]}
            </h4>
          );
        }
      }
      // Detect problematic sections in new format
      else if (trimmedLine.includes('Trecho relevante:') || trimmedLine.includes('Trecho problemático:')) {
        flushList();
        const text = trimmedLine.replace(/.*?(Trecho relevante|Trecho problemático):\s*/i, '').replace(/^["']|["']$/g, '').trim();
        elements.push(
          <div key={`trecho-${uniqueKey}`} className="mb-3 p-3 bg-[#1A1F3A] rounded-lg border-l-4 border-blue-500">
            <div className="font-semibold text-blue-400 mb-1">Trecho relevante:</div>
            <div className="text-gray-300 italic">"{text}"</div>
          </div>
        );
      }
      // Detect problem identification
      else if (trimmedLine.includes('Problema identificado:')) {
        flushList();
        const text = trimmedLine.replace(/.*?Problema identificado:\s*/i, '').trim();
        elements.push(
          <div key={`problema-${uniqueKey}`} className="mb-3 ml-4">
            <span className="font-semibold text-red-400">Problema identificado:</span>
            <span className="text-gray-300 ml-2">{parseMarkdown(text, uniqueKey)}</span>
          </div>
        );
      }
      // Detect explanation with sources
      else if (trimmedLine.includes('Explicação baseada em fontes:') || trimmedLine.includes('Explicação correta:')) {
        flushList();
        const text = trimmedLine.replace(/.*?(Explicação baseada em fontes|Explicacao correta):\s*/i, '').trim();
        elements.push(
          <div key={`explicacao-${uniqueKey}`} className="mb-4 ml-4">
            <span className="font-semibold text-green-400">Explicação baseada em fontes:</span>
            <span className="text-gray-300 ml-2">{parseMarkdown(text, uniqueKey)}</span>
          </div>
        );
      }
      // Detect positive aspects
      else if (trimmedLine.includes('Aspecto positivo:')) {
        flushList();
        const text = trimmedLine.replace(/.*?Aspecto positivo:\s*/i, '').trim();
        elements.push(
          <div key={`positivo-${uniqueKey}`} className="mb-3 ml-4">
            <span className="font-semibold text-green-400">Aspecto positivo:</span>
            <span className="text-gray-300 ml-2">{parseMarkdown(text, uniqueKey)}</span>
          </div>
        );
      }
      // Detect "Por que é positivo"
      else if (trimmedLine.includes('Por que é positivo:')) {
        flushList();
        const text = trimmedLine.replace(/.*?Por que é positivo:\s*/i, '').trim();
        elements.push(
          <div key={`porque-positivo-${uniqueKey}`} className="mb-4 ml-4">
            <span className="font-semibold text-green-400">Por que é positivo:</span>
            <span className="text-gray-300 ml-2">{parseMarkdown(text, uniqueKey)}</span>
          </div>
        );
      }
      // Detect list items
      else if (trimmedLine.match(/^[-•]\s+(.+)$/) || trimmedLine.match(/^\d+[\.\)]\s+(.+)$/)) {
        const match = trimmedLine.match(/^[-•]\s+(.+)$/) || trimmedLine.match(/^\d+[\.\)]\s+(.+)$/);
        if (match) {
          currentList.push(match[1]);
        }
      }
      // Sources section formatting
      else if (inSourcesSection && trimmedLine.match(/^\d+\.\s+(.+)$/)) {
        flushList();
        const match = trimmedLine.match(/^\d+\.\s+(.+)$/);
        if (match) {
          const sourceText = match[1];
          // Try to detect URLs
          const urlMatch = sourceText.match(/(https?:\/\/[^\s]+)/);
          if (urlMatch) {
            const beforeUrl = sourceText.substring(0, urlMatch.index);
            const url = urlMatch[1];
            const afterUrl = sourceText.substring((urlMatch.index || 0) + url.length);
            elements.push(
              <div key={`source-${uniqueKey}`} className="mb-2 text-gray-300">
                {beforeUrl && <span>{beforeUrl}</span>}
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">
                  {url}
                </a>
                {afterUrl && <span>{afterUrl}</span>}
              </div>
            );
          } else {
            elements.push(
              <div key={`source-${uniqueKey}`} className="mb-2 text-gray-300">
                {parseMarkdown(sourceText, uniqueKey)}
              </div>
            );
          }
        }
      }
      // Regular paragraphs
      else {
        flushList();
        if (trimmedLine.length > 0) {
          // Check if it's "aceitável" section - highlight it
          const isAcceptableSection = /aceitável|aceitavel|conteúdo adequado|sem problemas significativos/i.test(trimmedLine);
          
          if (isAcceptableSection) {
            elements.push(
              <div key={`para-${uniqueKey}`} className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-gray-300 leading-relaxed">{parseMarkdown(trimmedLine, uniqueKey)}</p>
              </div>
            );
          } else {
            elements.push(
              <p key={`para-${uniqueKey}`} className="text-gray-300 mb-4 leading-relaxed">
                {parseMarkdown(trimmedLine, uniqueKey)}
              </p>
            );
          }
        }
      }
    });

    flushList();

    return elements.length > 0 ? <div>{elements}</div> : <p className="text-gray-300">{parseMarkdown(content, `${sectionId || 'default'}-fallback`)}</p>;
  };

  const formatAnalysis = (text: string) => {
    // Split by numbered sections (1., 2., 3., 4.)
    const sectionRegex = /(\d+)\.\s*\*\*([^*]+)\*\*/g;
    const sections: Array<{ number: number; title: string; content: string }> = [];
    let match;

    // Find all section headers
    const sectionHeaders: Array<{ index: number; number: number; title: string; endIndex: number }> = [];
    while ((match = sectionRegex.exec(text)) !== null) {
      sectionHeaders.push({
        index: match.index,
        number: parseInt(match[1]),
        title: match[2],
        endIndex: match.index + match[0].length
      });
    }

    // Extract content for each section
    sectionHeaders.forEach((header, idx) => {
      const startIndex = header.endIndex;
      const endIndex = idx < sectionHeaders.length - 1 ? sectionHeaders[idx + 1].index : text.length;
      const content = text.substring(startIndex, endIndex).trim();
      
      sections.push({
        number: header.number,
        title: header.title,
        content
      });
    });

    // If no sections found with markdown headers, try plain numbered sections
    if (sections.length === 0) {
      const plainSections = text.split(/(?=\d+\.\s+[A-ZÁÉÍÓÚÃÕÇ])/).filter(Boolean);
      plainSections.forEach((section) => {
        const match = section.match(/^(\d+)\.\s+(.+?)(?:\n|$)/);
        if (match) {
          const title = match[2].trim();
          const content = section.substring(match[0].length).trim();
          sections.push({
            number: parseInt(match[1]),
            title,
            content
          });
        }
      });
    }

    return sections;
  };

  const handleCopyAnalysis = async () => {
    try {
      await navigator.clipboard.writeText(analysis.analysis_result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const sections = formatAnalysis(analysis.analysis_result);

  return (
    <div className="space-y-6">
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          onClick={handleCopyAnalysis}
          className="flex items-center gap-2"
        >
          {copied ? (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Copiado!
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copiar Análise
            </>
          )}
        </Button>
      </div>

      {sections.map((section) => {
        const isSourcesSection = section.title.toLowerCase().includes('fontes');
        
        return (
          <Card 
            key={section.number} 
            className={`p-6 ${isSourcesSection ? 'bg-[#1A1F3A]/50' : ''}`} 
            hover={!isSourcesSection}
          >
            <h3 className="text-xl font-bold mb-4 gradient-text">
              {section.number}. {section.title}
            </h3>
            <div className="prose prose-invert max-w-none">
              {formatContent(section.content, section.title, section.number)}
            </div>
            
            {isSourcesSection && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400 italic">
                  As fontes acima foram consultadas automaticamente durante a análise e podem incluir links externos.
                </p>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

